import React, { useState, useEffect } from 'react';
import { Table, Input, Select, Button, Tag, Spin, Empty, Tooltip } from 'antd';
import {
  SearchOutlined, CloseCircleOutlined, PlusOutlined, EyeOutlined, QrcodeOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { trainingService } from '@iso/services';
import SessionQrModal from './SessionQrModal';

const { Option } = Select;

const PAGE_SIZE = 12;

const getStatusTag = (status) => {
  const map = {
    compliant: { className: 'status-compliant', label: 'Competent' },
    expiring_soon: { className: 'status-expiring', label: 'Expiring Soon' },
    expired: { className: 'status-expired', label: 'Expired' },
    not_competent: { className: 'status-missing', label: 'Not Competent' },
  };
  const s = map[status] || { className: 'status-missing', label: status || 'Unknown' };
  return <Tag className={s.className}>{s.label}</Tag>;
};

const getDaysClass = (days) => {
  if (days === null || days === undefined) return '';
  if (days <= 0) return 'days-expired';
  if (days <= 30) return 'days-critical';
  if (days <= 90) return 'days-warning';
  return 'days-ok';
};

const TrainingRecords = ({ propertyId, onRunSession, canRunSession, refreshKey }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPersonType, setFilterPersonType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  // Which session's QR is on screen. Holds the id rather than a boolean so the
  // modal can load the session itself and stay ignorant of this table.
  const [qrSessionId, setQrSessionId] = useState(null);

  useEffect(() => {
    if (!propertyId) return;
    fetchRecords();
  }, [propertyId, currentPage, filterStatus, refreshKey]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE };
      if (filterStatus) params.status = filterStatus;
      const res = await trainingService.getComplianceData(propertyId, { ...params, page: currentPage });
      if (res) {
        setRecords(res.compliance_records || []);
        setTotalItems(res.total_item || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = records.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.person_name || '').toLowerCase().includes(q)
      || (r.training_name || '').toLowerCase().includes(q);
  }).filter(r => {
    if (!filterPersonType) return true;
    return r.person_type === filterPersonType;
  });

  const columns = [
    {
      title: 'Person Name',
      dataIndex: 'person_name',
      key: 'person_name',
      render: t => <span style={{ fontWeight: 600 }}>{t}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'person_type',
      key: 'person_type',
      render: t => <Tag>{t}</Tag>,
    },
    {
      title: 'Training',
      dataIndex: 'training_name',
      key: 'training_name',
    },
    {
      title: 'Completed Date',
      dataIndex: 'completed_date',
      key: 'completed_date',
      render: d => d ? moment(d).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: d => d ? moment(d).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Days Until Expiry',
      dataIndex: 'days_until_expiry',
      key: 'days_until_expiry',
      render: d => {
        if (d === null || d === undefined) return <span style={{ color: '#8c8c8c' }}>—</span>;
        return (
          <span className={getDaysClass(d)}>
            {d <= 0 ? `${Math.abs(d)} days overdue` : `${d} days`}
          </span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
    },
    {
      title: '',
      key: 'qr',
      width: 60,
      render: (_, record) => (
        // Every record was produced by a session, and that session's QR is how
        // anyone else joins it or sits its assessment — so the way back to the
        // code lives on the record itself.
        <Tooltip title="Show the QR code for this training session">
          <Button
            type="text"
            icon={<QrcodeOutlined />}
            disabled={!record.session_id}
            onClick={() => setQrSessionId(record.session_id)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="tab-content">
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div className="filter-row">
          <Input
            className="search-bar training-search-bar"
            prefix={<SearchOutlined />}
            suffix={search ? <CloseCircleOutlined onClick={() => setSearch('')} /> : null}
            placeholder="Search by person or training..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Select
            className="filter-select"
            value={filterPersonType}
            onChange={v => { setFilterPersonType(v); setCurrentPage(1); }}
            placeholder="All Person Types"
          >
            <Option value="">All Person Types</Option>
            <Option value="staff">Staff</Option>
            <Option value="visitor">Visitor</Option>
            <Option value="contractor">Contractor</Option>
          </Select>
          <Select
            className="filter-select"
            value={filterStatus}
            onChange={v => { setFilterStatus(v); setCurrentPage(1); }}
            placeholder="All Statuses"
          >
            <Option value="">All Statuses</Option>
            <Option value="compliant">Competent</Option>
            <Option value="expiring_soon">Expiring Soon</Option>
            <Option value="expired">Expired</Option>
            <Option value="not_competent">Not Competent</Option>
          </Select>
        </div>
        {canRunSession && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: '#0B2A1B', borderColor: '#0B2A1B', borderRadius: 8 }}
            onClick={onRunSession}
          >
            Run New Training Session
          </Button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : filteredRecords.length === 0 ? (
        <Empty description="No training records found" style={{ marginTop: 60 }} />
      ) : (
        <Table
          dataSource={filteredRecords}
          columns={columns}
          rowKey={(r, i) => `${r.person_id}_${r.training_asset_id}_${i}`}
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total: totalItems,
            onChange: setCurrentPage,
            showTotal: (total) => `${total} records`,
          }}
          scroll={{ x: 900 }}
        />
      )}
      <SessionQrModal
        visible={qrSessionId !== null}
        sessionId={qrSessionId}
        onClose={() => setQrSessionId(null)}
      />
    </div>
  );
};

export default TrainingRecords;
